import { useMemo, useState } from 'react';
import {
  Button,
  DataTableSkeleton,
  InlineNotification,
  Search,
} from '@carbon/react';
import { Add } from '@carbon/react/icons';
import type { WellboreMapData } from 'types/api-responses';
import { ReconcileSuggestionsTable } from './reconcile-suggestions-table';
import { ReconcileWellsMap } from './reconcile-wells-map';
import type { WellCandidate } from './consolidation-utils';
import styles from './reconcile-wells-tearsheet.module.scss';

/** Shown in place of the pool when its candidates/details fetch fails. */
const LOAD_ERROR_NOTIFICATION = (
  <InlineNotification
    kind="error"
    lowContrast
    hideCloseButton
    title="Could not load potential duplicates"
    subtitle="Close this panel and try again."
  />
);

export interface CurrentSuggestionsProps {
  /** The initial candidate/details fetch is still running. */
  loading: boolean;
  /** That fetch failed. */
  loadError: boolean;
  /** What the sheet shows: the fetched pool plus any wells added from the catalog. */
  displayPool: WellCandidate[];
  geometry: Record<string, WellboreMapData>;
  checkedIds: Set<string>;
  onToggle: (rowId: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  /** Opens the "Add wells to reconcile" catalog search. */
  onAddWells: () => void;
}

/**
 * The left panel of the Reconcile Wells sheet: the inset map over the suggestions table, with an
 * "Add wells" action that opens the catalog search. Checking a record here authors the reconcile.
 * @param {CurrentSuggestionsProps} props The pool, its load state, the checkbox state and the handlers.
 * @return {JSX.Element} The Current Suggestions panel.
 */
export function CurrentSuggestions({
  loading,
  loadError,
  displayPool,
  geometry,
  checkedIds,
  onToggle,
  onToggleAll,
  onAddWells,
}: CurrentSuggestionsProps) {
  // Narrows the displayed rows; the viewed well stays pinned regardless, and the selection (held by
  // rowId in the parent) is untouched, so a row filtered away keeps whatever check it had.
  const [search, setSearch] = useState('');
  const visiblePool = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return displayPool;
    return displayPool.filter(
      (well) =>
        well.isAnchor ||
        `${well.wellName} ${well.uwi} ${well.operator}`
          .toLowerCase()
          .includes(term)
    );
  }, [displayPool, search]);

  // Computed over the whole pool, not the search-filtered rows, so the header check-all box reflects
  // the global selection it toggles — otherwise a filtered view reading as "all checked" would fire
  // uncheck-all across the entire reconcile.
  const allChecked =
    displayPool.length > 0 &&
    displayPool.every((well) => checkedIds.has(well.rowId));

  // The search hid every potential duplicate (only the pinned Viewed Well is left), yet the pool does
  // have duplicates — tell the user the search, not the data, emptied the list.
  const searchHidAll =
    search.trim().length > 0 &&
    displayPool.length > 1 &&
    visiblePool.length <= 1;

  return (
    <section className={styles.left} aria-label="Current Suggestions">
      <h4 className={styles.sectionTitle}>Current Suggestions</h4>
      <p className={styles.sectionSubtitle}>
        The viewed well and its potential duplicates. Checking a record includes
        it in the reconcile — the first check seeds the survivor; later checks
        fold into it.
      </p>

      {loading ? (
        <DataTableSkeleton
          showHeader={false}
          showToolbar={false}
          columnCount={6}
          rowCount={3}
        />
      ) : loadError && displayPool.length < 2 ? (
        // Only when there is nothing to show: React Query keeps the prior pool on a failed background
        // refetch, so a populated sheet stays up — and catalog-added wells (in displayPool, not the
        // fetched pool) keep the panel up too, rather than being blanked behind the error.
        LOAD_ERROR_NOTIFICATION
      ) : (
        <>
          <ReconcileWellsMap pool={displayPool} geometry={geometry} />

          <div className={styles.suggestionsToolbar}>
            <Search
              size="sm"
              className={styles.suggestionsSearch}
              labelText="Search current suggestions"
              placeholder="Search by well name, UWI, or operator"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onClear={() => setSearch('')}
            />
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={Add}
              onClick={onAddWells}
            >
              Add wells
            </Button>
          </div>

          <ReconcileSuggestionsTable
            pool={visiblePool}
            checkedIds={checkedIds}
            allChecked={allChecked}
            onToggle={onToggle}
            onToggleAll={onToggleAll}
          />

          {searchHidAll && (
            <p className={styles.empty}>No suggestions match your search.</p>
          )}

          {displayPool.length < 2 && (
            <p className={styles.empty}>
              No open potential duplicates for this well. Use “Add wells” to
              pull in wells from the catalog.
            </p>
          )}
        </>
      )}
    </section>
  );
}
